import { FC, useContext, useMemo } from 'react';
import { Button, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { StyledRow, SaveAsDraftButton } from './Footer.styles';
import type { FooterProps } from './Footer.types';
import { NAMESPACE } from '@/constants';
import { ChannelFormContext } from '../../ChannelForm.context';

const Footer: FC<FooterProps> = ({
  step,
  stepsCount,
  isFormValid,
  isFormTouched,
  isSubmitting,
  handleNext,
  handlePrev,
  submit
}) => {
  const { warnings, channel } = useContext(ChannelFormContext);
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const isSaveAsDraftEnabled = !!channel && isFormTouched;
  const isSubmitDisabled = !isFormTouched || !isFormValid || isSubmitting || warnings.size > 0;

  const saveButtonTooltip = useMemo(() => {
    const messages: string[] = [];

    if (!isFormTouched) {
      return t('noChangesToSave');
    }

    if (warnings.size > 0) {
      warnings.forEach((warning) => messages.push(warning));
      const warning = t('pleaseFixErrors');
      if (messages.length > 1) {
        return `${warning}: ${messages.concat(', ')}`;
      }

      return `${warning}: ${messages[0]}`;
    }

    return undefined;
  }, [warnings, isFormTouched, t]);

  const saveAsDraftButtonTooltip = useMemo(() => {
    if (isFormValid && !isSubmitting && !isSaveAsDraftEnabled) {
      return t('noChangesToSave');
    }

    return undefined;
  }, [isFormValid, isSubmitting, isSaveAsDraftEnabled, t]);

  const renderSaveAsDraftButton = () => (
    <Tooltip title={saveAsDraftButtonTooltip}>
      <SaveAsDraftButton
        disabled={!isSaveAsDraftEnabled || isSubmitting}
        type="default"
        size="large"
        onClick={() => submit(true)}
      >
        {t('saveAsDraft')}
      </SaveAsDraftButton>
    </Tooltip>
  );

  return (
    <StyledRow step={step}>
      {!!step && (
        <Button size="large" onClick={handlePrev}>
          {t('back')}
        </Button>
      )}
      <Space>
        {step === stepsCount ? (
          <>
            {renderSaveAsDraftButton()}
            <Tooltip title={saveButtonTooltip}>
              <Button disabled={isSubmitDisabled} type="primary" size="large" onClick={() => submit(false)}>
                {t('save')}
              </Button>
            </Tooltip>
          </>
        ) : (
          <>
            {renderSaveAsDraftButton()}
            <Button type="primary" size="large" onClick={handleNext}>
              {t('continue')}
            </Button>
          </>
        )}
      </Space>
    </StyledRow>
  );
};

export default Footer;
